import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	platformAiBudgetQueryKey,
	platformCostSummaryQueryKey,
	platformSubscriptionQueryKey,
	platformUsageQueryKey,
	syncPlatformSubscription,
} from "@/api/platformBilling";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/platform-plan/update",
)({
	component: PlatformPlanUpdatePage,
});

/**
 * PlatformPlanUpdatePage - Syncs subscription after returning from Stripe portal
 * and redirects to platform plan settings page
 */
function PlatformPlanUpdatePage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const doSyncAndRedirect = useCallback(async () => {
		try {
			await syncPlatformSubscription(orgId);
			// Invalidate queries to refresh subscription data
			await queryClient.invalidateQueries({
				queryKey: platformSubscriptionQueryKey(orgId),
			});
			await queryClient.invalidateQueries({
				queryKey: platformUsageQueryKey(orgId),
			});
			// The sync may grant pending top-up credits; refresh the AI budget
			// breakdown and cost summary so the raised total reflects immediately.
			await queryClient.invalidateQueries({
				queryKey: platformAiBudgetQueryKey(orgId),
			});
			await queryClient.invalidateQueries({
				queryKey: platformCostSummaryQueryKey(orgId),
			});
		} catch (error) {
			console.error("Failed to sync subscription:", error);
		}
		// Always redirect to platform plan page, even on error
		navigate({
			to: "/app/org/$orgId/settings/platform-plan",
			params: { orgId },
		});
	}, [orgId, queryClient, navigate]);

	// Sync on mount and redirect
	useEffect(() => {
		doSyncAndRedirect();
	}, [doSyncAndRedirect]);

	return (
		<div className="max-w-2xl mx-auto px-4 py-16 text-center">
			<div className="bg-base-100 rounded-lg shadow-lg p-8">
				<div className="flex justify-center mb-6">
					<Loader2 className="h-20 w-20 text-primary animate-spin" />
				</div>
				<h1 className="text-3xl font-bold text-base-content mb-4">
					{t("platformPlan.update.syncing")}
				</h1>
				<p className="text-base-content/70">
					{t("platformPlan.update.syncingDescription")}
				</p>
			</div>
		</div>
	);
}
